import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language } from '../services/poiService';
import { getActiveLanguages } from '../services/api';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  selectedLanguage: Language | null;
  languages: Language[];
  setSelectedLanguage: (language: Language | null) => void;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [selectedLanguage, setSelectedLanguageState] = useState<Language | null>(null);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { i18n } = useTranslation();

  // Load languages from API on mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setIsLoading(true);
        const response = await getActiveLanguages();
        const languageList = response.data || [];
        setLanguages(languageList);
        
        // Try to restore saved language from localStorage
        const savedLanguageCode = localStorage.getItem('selected_language_code');
        if (savedLanguageCode) {
          const savedLanguage = languageList.find((lang: Language) => lang.code === savedLanguageCode);
          if (savedLanguage) {
            setSelectedLanguageState(savedLanguage);
            // Also update i18next language
            i18n.changeLanguage(savedLanguage.code);
          }
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        setLanguages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  // Save selected language to localStorage when it changes
  const setSelectedLanguage = (language: Language | null) => {
    setSelectedLanguageState(language);
    if (language) {
      localStorage.setItem('selected_language_code', language.code);
      // Update i18next language
      i18n.changeLanguage(language.code);
    } else {
      localStorage.removeItem('selected_language_code');
      // Reset to default language (English)
      i18n.changeLanguage('en');
    }
  };

  const value: LanguageContextType = {
    selectedLanguage,
    languages,
    setSelectedLanguage,
    isLoading,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};